// Backup passphrase policy (Implementation Plan v02 §6.4; key-custody.md "Backup
// file key"; I-1a Batch 6a, IP2-36 (d)).
//
// - At least 12 characters, or 5 words. Five words must still reach the app-lock
//   minimum (10 characters), so "a b c d e" is not a passphrase.
// - Then the same refusals as the app lock (lock/secretPolicy.ts): repeats,
//   sequences, common passphrases / PINs, dates.
// - It may equal the app-lock passphrase if the person chooses; the app never
//   keeps the lock passphrase, so the person types it again.
//
// Pure: no storage, no crypto. makeBackup checks again before it derives a key,
// so a screen bug cannot seal a file under a refused passphrase.

import { checkSecret, PASSPHRASE_MIN_LENGTH, type SecretRefusal } from '../lock/secretPolicy'

export const BACKUP_MIN_LENGTH = 12
export const BACKUP_MIN_WORDS = 5

export type BackupPassphraseCheck = { readonly ok: true } | { readonly ok: false; readonly refusal: SecretRefusal }

/** Words = runs of non-space characters. */
export function countWords(text: string): number {
  return text.split(/\s+/u).filter((word) => word.length > 0).length
}

/** Check a new backup passphrase. Characters are counted as the person sees them (code points, NFC). */
export function checkBackupPassphrase(passphrase: string): BackupPassphraseCheck {
  const text = passphrase.normalize('NFC')
  const length = [...text].length
  const longEnough = length >= BACKUP_MIN_LENGTH
  const enoughWords = countWords(text) >= BACKUP_MIN_WORDS && length >= PASSPHRASE_MIN_LENGTH
  if (!longEnough && !enoughWords) {
    return { ok: false, refusal: 'too-short' }
  }
  const lock = checkSecret(text)
  return lock.ok ? { ok: true } : { ok: false, refusal: lock.refusal }
}
